import { useLanguage } from "@/contexts/LanguageContext";
import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft, Cookie, Shield, BarChart3, Megaphone, Settings } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useCookieConsent } from "@/hooks/useCookieConsent";

const translations = {
  en: {
    title: "Cookie Policy",
    subtitle: "How we use cookies on AcquizGroup",
    lastUpdated: "Last updated: February 2026",
    backToForm: "Back",
    manageCookies: "Manage My Cookies",
    intro: {
      title: "What are cookies?",
      description: "Cookies are small text files stored on your device when you visit our website. They help us improve your experience, remember your preferences, and understand how you use our platform.",
    },
    categories: {
      title: "Types of cookies we use",
      essential: {
        title: "Essential Cookies",
        description: "These cookies are necessary for the website to function properly. They enable core functionality such as security, authentication, and session management.",
        examples: [
          "Authentication tokens (keeping you logged in)",
          "Session identifiers",
          "Security tokens (CSRF protection)",
          "Cookie consent preferences",
        ],
        canDisable: "Cannot be disabled",
      },
      analytics: {
        title: "Analytics Cookies",
        description: "These cookies help us understand how visitors interact with our website by collecting and reporting information anonymously.",
        examples: [
          "Google Analytics (_ga, _gid)",
          "Page view tracking",
          "User journey analysis",
          "Performance metrics",
        ],
        canDisable: "Can be disabled",
      },
      marketing: {
        title: "Marketing Cookies",
        description: "These cookies are used to track visitors across websites to display relevant advertisements.",
        examples: [
          "Facebook Pixel",
          "Google Ads remarketing",
          "LinkedIn Insight Tag",
          "Conversion tracking",
        ],
        canDisable: "Can be disabled",
      },
      functional: {
        title: "Functional Cookies",
        description: "These cookies enable enhanced functionality and personalization, such as remembering your language preference.",
        examples: [
          "Language preferences",
          "Theme preferences (dark/light mode)",
          "Recently viewed items",
          "Form auto-fill data",
        ],
        canDisable: "Can be disabled",
      },
    },
    thirdParty: {
      title: "Third-party cookies",
      description: "Some cookies are placed by third-party services that appear on our pages. We do not control these cookies. Here are the third parties we work with:",
      parties: [
        { name: "Google Analytics", purpose: "Website analytics and performance monitoring" },
        { name: "Firebase", purpose: "Authentication, database and application services" },
      ],
    },
    rights: {
      title: "Your rights",
      description: "Under GDPR and other privacy regulations, you have the right to:",
      list: [
        "Accept or reject non-essential cookies",
        "Change your preferences at any time",
        "Request information about cookies we use",
        "Delete cookies stored on your device",
      ],
    },
    contact: {
      title: "Questions?",
      description: "If you have any questions about our use of cookies, please contact us at contact@acquizgroup.com",
    },
  },
  fr: {
    title: "Politique des Cookies",
    subtitle: "Comment nous utilisons les cookies sur AcquizGroup",
    lastUpdated: "Dernière mise à jour : Février 2026",
    backToForm: "Retour",
    manageCookies: "Gérer mes cookies",
    intro: {
      title: "Qu'est-ce qu'un cookie ?",
      description: "Les cookies sont de petits fichiers texte stockés sur votre appareil lorsque vous visitez notre site web. Ils nous aident à améliorer votre expérience, à mémoriser vos préférences et à comprendre comment vous utilisez notre plateforme.",
    },
    categories: {
      title: "Types de cookies que nous utilisons",
      essential: {
        title: "Cookies Essentiels",
        description: "Ces cookies sont nécessaires au bon fonctionnement du site. Ils permettent des fonctionnalités de base comme la sécurité, l'authentification et la gestion de session.",
        examples: [
          "Jetons d'authentification (maintien de connexion)",
          "Identifiants de session",
          "Jetons de sécurité (protection CSRF)",
          "Préférences de consentement aux cookies",
        ],
        canDisable: "Ne peuvent pas être désactivés",
      },
      analytics: {
        title: "Cookies Analytiques",
        description: "Ces cookies nous aident à comprendre comment les visiteurs interagissent avec notre site en collectant et rapportant des informations de manière anonyme.",
        examples: [
          "Google Analytics (_ga, _gid)",
          "Suivi des pages vues",
          "Analyse du parcours utilisateur",
          "Métriques de performance",
        ],
        canDisable: "Peuvent être désactivés",
      },
      marketing: {
        title: "Cookies Marketing",
        description: "Ces cookies sont utilisés pour suivre les visiteurs sur les sites web afin d'afficher des publicités pertinentes.",
        examples: [
          "Facebook Pixel",
          "Remarketing Google Ads",
          "LinkedIn Insight Tag",
          "Suivi des conversions",
        ],
        canDisable: "Peuvent être désactivés",
      },
      functional: {
        title: "Cookies Fonctionnels",
        description: "Ces cookies permettent des fonctionnalités améliorées et la personnalisation, comme la mémorisation de votre préférence de langue.",
        examples: [
          "Préférences de langue",
          "Préférences de thème (mode sombre/clair)",
          "Éléments récemment consultés",
          "Données de remplissage automatique",
        ],
        canDisable: "Peuvent être désactivés",
      },
    },
    thirdParty: {
      title: "Cookies tiers",
      description: "Certains cookies sont placés par des services tiers qui apparaissent sur nos pages. Nous ne contrôlons pas ces cookies. Voici les tiers avec lesquels nous travaillons :",
      parties: [
        { name: "Google Analytics", purpose: "Analyse du site web et suivi des performances" },
        { name: "Firebase", purpose: "Authentification, base de données et services applicatifs" },
      ],
    },
    rights: {
      title: "Vos droits",
      description: "Conformément au RGPD et aux autres réglementations sur la vie privée, vous avez le droit de :",
      list: [
        "Accepter ou refuser les cookies non essentiels",
        "Modifier vos préférences à tout moment",
        "Demander des informations sur les cookies que nous utilisons",
        "Supprimer les cookies stockés sur votre appareil",
      ],
    },
    contact: {
      title: "Des questions ?",
      description: "Si vous avez des questions concernant notre utilisation des cookies, contactez-nous à contact@acquizgroup.com",
    },
  },
};

const CookieCategory = ({
  icon: Icon,
  title,
  description,
  examples,
  canDisable,
  disabled = false,
}: {
  icon: React.ElementType;
  title: string;
  description: string;
  examples: string[];
  canDisable: string;
  disabled?: boolean;
}) => (
  <div className={`rounded-xl border p-6 ${disabled ? "bg-muted/30" : "bg-card"}`}>
    <div className="flex items-start gap-4">
      <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-accent/20">
        <Icon className="h-6 w-6 text-accent" />
      </div>
      <div className="flex-1">
        <div className="flex items-center justify-between">
          <h3 className="font-semibold text-lg text-foreground">{title}</h3>
          <span className={`text-xs px-2 py-1 rounded-full ${disabled ? "bg-muted text-muted-foreground" : "bg-accent/20 text-accent"}`}>
            {canDisable}
          </span>
        </div>
        <p className="mt-2 text-sm text-muted-foreground">{description}</p>
        <ul className="mt-3 space-y-1">
          {examples.map((example, index) => (
            <li key={index} className="text-sm text-muted-foreground flex items-center gap-2">
              <span className="h-1.5 w-1.5 rounded-full bg-accent" />
              {example}
            </li>
          ))}
        </ul>
      </div>
    </div>
  </div>
);

export default function CookiesPolicy() {
  const { language } = useLanguage();
  const navigate = useNavigate();
  const { resetConsent } = useCookieConsent();
  const t = translations[language];

  const handleManageCookies = () => {
    resetConsent();
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="bg-primary text-primary-foreground py-16">
        <div className="max-w-4xl mx-auto px-6">
          <Button
            variant="ghost"
            className="mb-6 text-primary-foreground/80 hover:text-primary-foreground hover:bg-white/10"
            onClick={() => navigate(-1)}
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            {t.backToForm}
          </Button>
          <div className="flex items-center gap-4 mb-4">
            <div className="flex h-14 w-14 items-center justify-center rounded-full bg-white/10">
              <Cookie className="h-7 w-7 text-accent" />
            </div>
            <div>
              <h1 className="text-3xl md:text-4xl font-bold">{t.title}</h1>
              <p className="text-primary-foreground/80">{t.subtitle}</p>
            </div>
          </div>
          <p className="text-sm text-primary-foreground/60">{t.lastUpdated}</p>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-6 py-12 space-y-12">
        {/* Introduction */}
        <section>
          <h2 className="text-2xl font-bold text-foreground mb-4">{t.intro.title}</h2>
          <p className="text-muted-foreground">{t.intro.description}</p>
        </section>

        {/* Cookie Categories */}
        <section>
          <h2 className="text-2xl font-bold text-foreground mb-6">{t.categories.title}</h2>
          <div className="space-y-4">
            <CookieCategory
              icon={Shield}
              title={t.categories.essential.title}
              description={t.categories.essential.description}
              examples={t.categories.essential.examples}
              canDisable={t.categories.essential.canDisable}
              disabled
            />
            <CookieCategory
              icon={BarChart3}
              title={t.categories.analytics.title}
              description={t.categories.analytics.description}
              examples={t.categories.analytics.examples}
              canDisable={t.categories.analytics.canDisable}
            />
            <CookieCategory
              icon={Megaphone}
              title={t.categories.marketing.title}
              description={t.categories.marketing.description}
              examples={t.categories.marketing.examples}
              canDisable={t.categories.marketing.canDisable}
            />
            <CookieCategory
              icon={Settings}
              title={t.categories.functional.title}
              description={t.categories.functional.description}
              examples={t.categories.functional.examples}
              canDisable={t.categories.functional.canDisable}
            />
          </div>
        </section>

        {/* Third Party */}
        <section>
          <h2 className="text-2xl font-bold text-foreground mb-4">{t.thirdParty.title}</h2>
          <p className="text-muted-foreground mb-4">{t.thirdParty.description}</p>
          <div className="overflow-hidden rounded-lg border">
            <table className="w-full text-sm">
              <thead className="bg-muted">
                <tr>
                  <th className="px-4 py-3 text-left font-medium">Service</th>
                  <th className="px-4 py-3 text-left font-medium">Purpose</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {t.thirdParty.parties.map((party, index) => (
                  <tr key={index}>
                    <td className="px-4 py-3 font-medium">{party.name}</td>
                    <td className="px-4 py-3 text-muted-foreground">{party.purpose}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>

        {/* Rights */}
        <section>
          <h2 className="text-2xl font-bold text-foreground mb-4">{t.rights.title}</h2>
          <p className="text-muted-foreground mb-4">{t.rights.description}</p>
          <ul className="space-y-2">
            {t.rights.list.map((item, index) => (
              <li key={index} className="flex items-center gap-3 text-muted-foreground">
                <span className="h-2 w-2 rounded-full bg-accent" />
                {item}
              </li>
            ))}
          </ul>
        </section>

        {/* Manage Cookies CTA */}
        <section className="rounded-xl bg-accent/10 border border-accent/20 p-6 text-center">
          <h3 className="text-xl font-bold text-foreground mb-2">{t.manageCookies}</h3>
          <Button onClick={handleManageCookies} className="mt-4 bg-primary hover:bg-primary-hover">
            <Settings className="mr-2 h-4 w-4" />
            {t.manageCookies}
          </Button>
        </section>

        {/* Contact */}
        <section>
          <h2 className="text-2xl font-bold text-foreground mb-4">{t.contact.title}</h2>
          <p className="text-muted-foreground">{t.contact.description}</p>
        </section>

        {/* Footer Links */}
        <footer className="flex flex-wrap gap-4 pt-8 border-t text-sm text-muted-foreground">
          <Link to="/terms" className="hover:text-accent">
            {language === "fr" ? "Termes et Conditions" : "Terms & Conditions"}
          </Link>
          <span>•</span>
          <Link to="/privacy" className="hover:text-accent">
            {language === "fr" ? "Politique de confidentialité" : "Privacy Policy"}
          </Link>
        </footer>
      </main>
    </div>
  );
}
